import { getTenantBasePath } from '@low6-pickem/frontend-shared';
import type { LobbyGame } from '../api/getLobby';
import type { DRDaySummary, DRGetGameResponse } from '../api/getMyGames';

/** Navigate out of the lobby (full page — matches LobbyView game selection). */
export function navigateToGameUrl(url: string | null | undefined): void {
  if (!url) return;
  window.location.href = url;
}

export function isDailyRevealLobbyGame(game: LobbyGame): boolean {
  const gt = (game.game_type ?? '').trim().toLowerCase();
  return gt === 'daily_reveal' || gt === 'daily_draw';
}

function stripDailyRevealGamePath(pathname: string): string {
  // Handle both `/game/:id` and `/game/:id/` — a trailing slash used to leave the
  // contest segment in place, so `${origin}/game/${otherId}` nested under the wrong week.
  return pathname.replace(/\/game\/[^/]+\/?$/, '').replace(/\/$/, '');
}

/** Daily Reveal app tenant root, e.g. https://reveal-prod.low6.com/tenant/dk */
export function buildDailyRevealOrigin(lobbyGames: LobbyGame[]): string | null {
  const dr = lobbyGames.find(g => isDailyRevealLobbyGame(g) && g.public_url);
  if (!dr?.public_url) return null;

  try {
    const parsed = new URL(dr.public_url);
    return `${parsed.origin}${stripDailyRevealGamePath(parsed.pathname)}`;
  } catch {
    return stripDailyRevealGamePath(dr.public_url);
  }
}

/** Live tab — main app entry; active contest is selected automatically. */
export function buildDailyRevealEntryUrl(lobbyGames: LobbyGame[]): string | null {
  const origin = buildDailyRevealOrigin(lobbyGames);
  return origin ? `${origin}/` : null;
}

/**
 * Deep-link to a specific Daily Reveal contest (`/game/:gameId`).
 * Always names `gameId` in the path — never reuse another contest's `/game/`
 * segment from a shared lobby public_url (that sent NFL cards into the MLB week).
 */
export function buildDailyRevealContestUrl(gameId: string, lobbyGames: LobbyGame[]): string | null {
  if (!gameId) return null;

  const exact = lobbyGames.find(g => isDailyRevealLobbyGame(g) && g.pickem_id === gameId);
  if (exact?.public_url) {
    try {
      const parsed = new URL(exact.public_url);
      parsed.pathname = `${stripDailyRevealGamePath(parsed.pathname)}/game/${gameId}`;
      return parsed.toString();
    } catch {
      const [path, rest] = exact.public_url.split(/(?=[?#])/, 2);
      return `${stripDailyRevealGamePath(path)}/game/${gameId}${rest ?? ''}`;
    }
  }

  const origin = buildDailyRevealOrigin(lobbyGames);
  if (!origin) return null;
  return `${origin}/game/${gameId}`;
}

/**
 * Make a Daily Reveal lobby link name its contest (DKPLAT-763).
 *
 * A contest whose `public_url` is just the app root (or the tenant root) leaves the
 * choice of contest to the Daily Reveal app, which then resolves the globally active
 * week — so a tile for one contest could open another. When the URL has no
 * `/game/:id` segment we append the card's own contest id; a URL that already names
 * a contest is left exactly as configured.
 */
export function ensureDailyRevealContestUrl(game: LobbyGame): string | null {
  const publicUrl = game.public_url;
  if (!publicUrl || !isDailyRevealLobbyGame(game) || !game.pickem_id) return publicUrl ?? null;
  if (/\/game\/[^/?#]+/.test(publicUrl)) return publicUrl;

  try {
    const parsed = new URL(publicUrl);
    parsed.pathname = `${parsed.pathname.replace(/\/$/, '')}/game/${game.pickem_id}`;
    return parsed.toString();
  } catch {
    // Relative public_url — split off any query/hash before appending the segment.
    const [path, rest] = publicUrl.split(/(?=[?#])/, 2);
    return `${path.replace(/\/$/, '')}/game/${game.pickem_id}${rest ?? ''}`;
  }
}

/** Resolve which Daily Reveal contest contains a given day (for card navigation). */
export function findDRGameIdForDay(
  day: DRDaySummary,
  drGamesData: Array<DRGetGameResponse | undefined>
): string | null {
  for (const game of drGamesData) {
    if (!game) continue;
    // Match on day id only. A same-date fallback used to return the first contest
    // that shared the calendar day (e.g. MLB when tapping an NFL card).
    if (game.days.some(d => d.id === day.id)) return game.game.id;
  }
  return null;
}

export function isSurvivorLobbyGame(game: LobbyGame): boolean {
  return (game.game_type ?? '').trim().toLowerCase() === 'survivor';
}

/**
 * Resolve a Survivor play URL. Survivor routes on `/game/:pickemId`, the same shape as
 * Pick'em, so a contest missing from the lobby listing can borrow another Survivor card's
 * origin and tenant path.
 *
 * Unlike `resolvePickemPublicUrl` there is NO same-origin fallback: Survivor is a separately
 * deployed app, so without a Survivor URL to pattern-match there is nothing to guess from —
 * the card renders unlinked rather than sending the player to a lobby-origin 404.
 */
export function resolveSurvivorPublicUrl(pickemId: string, lobbyGames: LobbyGame[]): string | null {
  const exact = lobbyGames.find(g => g.pickem_id === pickemId && g.public_url);
  if (exact?.public_url) return exact.public_url;

  const template = lobbyGames.find(g => isSurvivorLobbyGame(g) && g.public_url);
  if (!template?.public_url) return null;

  try {
    const parsed = new URL(template.public_url);
    const tenantBase = parsed.pathname.replace(/\/game\/[^/]+$/, '');
    return `${parsed.origin}${tenantBase}/game/${pickemId}`;
  } catch {
    // Relative public_url — same origin as the lobby.
    const tenantBase = template.public_url.replace(/\/game\/[^/]+$/, '');
    return `${tenantBase}/game/${pickemId}`;
  }
}

export function isBingoLobbyGame(game: LobbyGame): boolean {
  return (game.game_type ?? '').trim().toLowerCase() === 'bingo';
}

/**
 * Resolve a Game Grid (Bingo) play URL. Bingo routes on `/game/:pickemId`, the same shape as
 * Pick'em and Survivor, so a contest missing from the lobby listing can borrow another Bingo
 * card's origin and tenant path.
 *
 * No same-origin fallback, for the same reason Survivor has none: Bingo is a separately deployed
 * app, so without a Bingo URL to pattern-match there is nothing to guess from — the card renders
 * unlinked rather than sending the player to a lobby-origin 404.
 */
export function resolveBingoPublicUrl(pickemId: string, lobbyGames: LobbyGame[]): string | null {
  const exact = lobbyGames.find(g => g.pickem_id === pickemId && g.public_url);
  if (exact?.public_url) return exact.public_url;

  const template = lobbyGames.find(g => isBingoLobbyGame(g) && g.public_url);
  if (!template?.public_url) return null;

  try {
    const parsed = new URL(template.public_url);
    const tenantBase = parsed.pathname.replace(/\/game\/[^/]+$/, '');
    return `${parsed.origin}${tenantBase}/game/${pickemId}`;
  } catch {
    // Relative public_url — same origin as the lobby.
    const tenantBase = template.public_url.replace(/\/game\/[^/]+$/, '');
    return `${tenantBase}/game/${pickemId}`;
  }
}

/**
 * Resolve a Pick'em play URL when the contest is not in the current lobby game list.
 * Uses another pick'em card's origin/path pattern when available.
 */
export function resolvePickemPublicUrl(pickemId: string, lobbyGames: LobbyGame[]): string | null {
  const exact = lobbyGames.find(g => g.pickem_id === pickemId && g.public_url);
  if (exact?.public_url) return exact.public_url;

  const template = lobbyGames.find(g => g.game_type === 'pickem' && g.public_url);
  if (template?.public_url) {
    try {
      const parsed = new URL(template.public_url);
      const tenantBase = parsed.pathname.replace(/\/game\/[^/]+$/, '');
      return `${parsed.origin}${tenantBase}/game/${pickemId}`;
    } catch {
      // Relative public_url — same origin as lobby
      const tenantBase = template.public_url.replace(/\/game\/[^/]+$/, '');
      return `${tenantBase}/game/${pickemId}`;
    }
  }

  const tenantPath = (getTenantBasePath() ?? '').replace(/\/$/, '');
  return tenantPath ? `${tenantPath}/game/${pickemId}` : null;
}
